import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import optionApi from '../../../services/optionApi';
import { PlusIcon, MagnifyingGlassIcon } from '@heroicons/react/20/solid';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import OptionGroupUpsertModal from './components/option-group-upsert-modal';
import SlideOver from '../../../components/slide-over/slide-over';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export default function Modifiers({ show, onClose, preferredGroupId = null }) {
  const globalCompany = useSelector((state) => state.companyReducer.selected);
  const appliedPreferredRef = useRef(false);
  const [dialogs, setDialogs] = useState({
    optionGroupUpsert: false,
    optionUpsert: false,
    optionVisible: false,
    hidedateDelete: false,
    optionGroupDelete: false,
  });

  const [errors, setErrors] = useState({
    optionGroup: { name: '' },
    option: { name: '', price: '' },
  });

  const [optionGroupList, setOptionGroupList] = useState([]);
  const [filteredList, setFilteredList] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOptionGroup, setSelectedOptionGroup] = useState({
    name: '',
    description: '',
    companyid: globalCompany.id,
    options: [],
    storeids: [],
    allowquantities: false,
  });
  const [loading, setLoading] = useState({
    optionGroupList: false,
    options: true,
    upsertLoading: false,
    deleteLoading: false,
    visibleLoading: false,
    storeLoading: false,
  });
  const [mobileShowDetail, setMobileShowDetail] = useState(false);

  const getOptionGroupList = useCallback(async () => {
    try {
      setLoading((prev) => ({ ...prev, optionGroupList: true }));
      const query = `?companyid=${globalCompany.id}&pageSize=1000&isdeleted=false`;
      const res = await optionApi.groupList(query);
      if (res) {
        const mapped = res.data.data.map((group) => ({
          ...group,
          storeids:
            group.optionGroupStores?.map((store) => ({
              value: store.storeid,
              label: store.store?.name,
            })) || [],
        }));
        setOptionGroupList(mapped);
        setFilteredList(mapped);
        if (preferredGroupId && !appliedPreferredRef.current) {
          appliedPreferredRef.current = true;
          const match = mapped.find((g) => g.id === preferredGroupId);
          if (match) {
            await handleOptionGroupClick(match);
          }
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading((prev) => ({ ...prev, optionGroupList: false }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalCompany]);

  useEffect(() => {
    if (globalCompany?.id) {
      getOptionGroupList();
    }
  }, [globalCompany, getOptionGroupList]);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredList(optionGroupList);
    } else {
      const q = searchQuery.toLowerCase();
      setFilteredList(
        optionGroupList.filter((g) => g.name?.toLowerCase().includes(q)),
      );
    }
  }, [searchQuery, optionGroupList]);

  const getOptionsByOptionGroupId = async (optionGroupId) => {
    try {
      setLoading((prev) => ({ ...prev, options: true }));
      const res = await optionApi.list(optionGroupId);
      if (res) {
        setSelectedOptionGroup((prev) => ({
          ...prev,
          options: res.data.data,
        }));
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading((prev) => ({ ...prev, options: false }));
    }
  };

  const handleOptionGroupClick = async (group) => {
    setSelectedOptionGroup(group);
    await getOptionsByOptionGroupId(group.id);
    setDialogs((prev) => ({ ...prev, optionGroupUpsert: true }));
    setMobileShowDetail(true);
  };

  const onClickOptionGroupFormSubmit = async () => {
    if (
      !selectedOptionGroup.name ||
      selectedOptionGroup.min === '' ||
      selectedOptionGroup.max === ''
    ) {
      setErrors((prev) => ({
        ...prev,
        name: !selectedOptionGroup.name,
        min: selectedOptionGroup.min === '',
        max: selectedOptionGroup.max === '',
      }));
      return;
    } else {
      setErrors((prev) => ({ ...prev, name: false, min: false, max: false }));
    }
    try {
      setLoading((prev) => ({ ...prev, upsertLoading: true }));
      const body = {
        ...selectedOptionGroup,
        name: selectedOptionGroup.name
          ? selectedOptionGroup.name.trim()
          : selectedOptionGroup.name.trim(),
        storeids: Array.isArray(selectedOptionGroup.storeids)
          ? selectedOptionGroup.storeids.map((store) => store.value ?? store)
          : [],
      };
      const optionGroup = await optionApi.upsertGroup(body);

      for (const item of selectedOptionGroup.options) {
        const optionBody = {
          ...item,
          optiongroupid: optionGroup.data.data.id,
          name: item.name ? item.name.trim() : item.name,
        };
        await optionApi.upsert(optionBody);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setSelectedOptionGroup((prev) => ({ ...prev, options: [] }));
      getOptionGroupList();
      setLoading((prev) => ({ ...prev, upsertLoading: false }));
      setDialogs((prev) => ({ ...prev, optionGroupUpsert: false }));
    }
  };

  const onClickCreateOptionGroup = () => {
    setSelectedOptionGroup({
      name: '',
      description: '',
      companyid: globalCompany.id,
      min: 0,
      max: 0,
      displayorder: 0,
      options: [],
      storeids: [],
      allowquantities: false,
    });
    setDialogs((prev) => ({ ...prev, optionGroupUpsert: true }));
    setMobileShowDetail(true);
  };

  const onMobileBack = () => {
    setMobileShowDetail(false);
    setDialogs((prev) => ({ ...prev, optionGroupUpsert: false }));
  };

  const listPanel = (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex items-center gap-2 border-b border-slate-200 px-4 py-3 dark:border-slate-700">
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search groups..."
            className="block w-full rounded-md border border-slate-300 bg-white py-1.5 pl-8 pr-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 dark:placeholder:text-slate-400 dark:focus:border-sky-400 dark:focus:ring-sky-400"
          />
        </div>
        <button
          type="button"
          onClick={onClickCreateOptionGroup}
          className="inline-flex flex-shrink-0 items-center gap-1 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-blue-700 dark:bg-sky-500 dark:hover:bg-sky-400"
        >
          <PlusIcon className="h-4 w-4" />
          New
        </button>
      </div>

      <div className="flex-1 overflow-y-auto hide-scrollbar">
        {loading.optionGroupList ? (
          <div className="space-y-2 p-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="h-14 animate-pulse rounded-md bg-slate-100 dark:bg-slate-700"
              />
            ))}
          </div>
        ) : filteredList.length === 0 ? (
          <div className="px-4 py-10 text-center">
            <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">
              {searchQuery ? 'No results' : 'No modifier groups'}
            </p>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              {searchQuery
                ? 'Try a different search term.'
                : 'Create your first modifier group to get started.'}
            </p>
          </div>
        ) : (
          <div className="space-y-1 p-2">
            {filteredList.map((group) => {
              const isActive =
                selectedOptionGroup?.id === group.id &&
                dialogs.optionGroupUpsert;
              return (
                <button
                  key={group.id}
                  type="button"
                  onClick={() => handleOptionGroupClick(group)}
                  className={classNames(
                    isActive
                      ? 'border-blue-500 bg-blue-50 dark:border-sky-400 dark:bg-slate-700'
                      : 'border-transparent hover:bg-slate-50 dark:hover:bg-slate-700/60',
                    'flex w-full items-center gap-3 rounded-md border px-3 py-2.5 text-left transition',
                  )}
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-slate-800 dark:text-slate-100">
                      {group.name}
                    </p>
                    {group.optionGroupStores?.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {group.optionGroupStores.map((store) => (
                          <span
                            key={store.storeid}
                            style={{ backgroundColor: store.store?.sitecolor }}
                            className="inline-flex rounded-full px-2 py-0.5 text-[0.6rem] font-medium text-white"
                          >
                            {store.store?.name}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <ChevronRightIcon className="h-4 w-4 flex-shrink-0 text-slate-400" />
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="border-t border-slate-200 px-4 py-2 dark:border-slate-700">
        <p className="text-xs text-slate-500 dark:text-slate-400">
          {optionGroupList.length} group
          {optionGroupList.length !== 1 ? 's' : ''} total
        </p>
      </div>
    </div>
  );

  const detailPanel = (
    <div className="flex min-h-0 flex-1 flex-col">
      {loading.options && dialogs.optionGroupUpsert ? (
        <div className="flex flex-1 items-center justify-center">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-slate-300 border-t-blue-600" />
        </div>
      ) : dialogs.optionGroupUpsert ? (
        <div className="flex-1 overflow-y-auto hide-scrollbar">
          <OptionGroupUpsertModal
            show={dialogs.optionGroupUpsert}
            optionGroupForm={selectedOptionGroup}
            setOptionGroupForm={setSelectedOptionGroup}
            getOptionGroupList={getOptionGroupList}
            dialogs={dialogs}
            setDialogs={setDialogs}
            loading={loading}
            errors={errors}
            setErrors={setErrors}
            setLoading={setLoading}
            onClickOptionGroupFormSubmit={onClickOptionGroupFormSubmit}
          />
        </div>
      ) : (
        <div className="flex flex-1 items-center justify-center px-6 py-10">
          <div className="text-center">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-700">
              <ChevronRightIcon className="h-6 w-6 text-slate-400" />
            </div>
            <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">
              Select a group
            </p>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              Choose a modifier group from the list to view and edit.
            </p>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <SlideOver
      show={show}
      onClose={onClose}
      title="Modifiers Management"
      subtitle="Create, edit and organize modifier groups across your menu."
      maxWidth="max-w-5xl"
    >
      <div className="flex min-h-0 flex-1 flex-col lg:flex-row -mx-4 -my-4 h-[calc(100vh-6rem)] sm:-mx-6 sm:-my-5">
        <div
          className={classNames(
            mobileShowDetail ? 'hidden lg:flex' : 'flex',
            'w-full flex-col border-b border-slate-200 lg:w-80 lg:flex-shrink-0 lg:border-b-0 lg:border-r dark:border-slate-700',
          )}
        >
          {listPanel}
        </div>
        <div
          className={classNames(
            mobileShowDetail ? 'flex' : 'hidden lg:flex',
            'min-h-0 flex-1 flex-col',
          )}
        >
          <div className="flex items-center gap-2 border-b border-slate-200 px-3 py-2 lg:hidden dark:border-slate-700">
            <button
              type="button"
              onClick={onMobileBack}
              className="inline-flex h-7 w-7 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-600"
            >
              <ChevronLeftIcon className="h-5 w-5" />
            </button>
            <p className="truncate text-sm font-semibold text-slate-800 dark:text-slate-100">
              {selectedOptionGroup?.name || 'New Group'}
            </p>
          </div>
          {detailPanel}
        </div>
      </div>
    </SlideOver>
  );
}
